import { useCallback, useEffect, useState } from 'react';

import { setStorageValue } from 'App/Helpers';

import { getPWAState } from '../helpers';

/**
 * Reusable PWA install-prompt lifecycle: deferred beforeinstallprompt,
 * status persistence, and showInstallPrompt handling.
 *
 * Phase gating (showPwaPhase / view counts) lives in {@link import('../PwaPhaseGate').PwaPhaseGate}.
 *
 * @param {string} storageKey - Base storage namespace for `pwa.status`.
 * @returns {{
 *   pwaStatus: string,
 *   deferredPwaPrompt: BeforeInstallPromptEvent | null,
 *   pwaPromptAttempt: number,
 *   showInstallPrompt: (params: {
 *     deferredPrompt: BeforeInstallPromptEvent | null,
 *     updatePushLead?: (payload: { pwa_install: string }) => void,
 *   }) => Promise<{
 *     gameGaEvent: string,
 *     gaEvent: string,
 *   }>,
 * }}
 */
export const usePwaInstallPrompt = (storageKey) => {
    const [pwaStatus, setPwaStatus] = useState(() => getPWAState(storageKey));
    const [deferredPwaPrompt, setDeferredPwaPrompt] = useState(null);
    const [pwaPromptAttempt, setPwaPromptAttempt] = useState(0);

    const updatePwaStatus = useCallback(
        (value) => {
            setPwaStatus(value);
            setStorageValue(storageKey, 'pwa.status', value);
        },
        [storageKey]
    );

    const recordPromptEngagement = useCallback(() => {
        setPwaPromptAttempt((n) => n + 1);
    }, []);

    useEffect(() => {
        if (pwaStatus !== 'NotInstalled') {
            return undefined;
        }

        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPwaPrompt(e);
        };

        const handleAppInstalled = () => {
            setDeferredPwaPrompt(null);
        };

        window.addEventListener(
            'beforeinstallprompt',
            handleBeforeInstallPrompt
        );
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener(
                'beforeinstallprompt',
                handleBeforeInstallPrompt
            );
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, [pwaStatus]);

    const showInstallPrompt = useCallback(
        async ({ deferredPrompt, updatePushLead }) => {
            if (!deferredPrompt) {
                updatePwaStatus('NotInstalled');
                recordPromptEngagement();

                return {
                    gameGaEvent: 'PWA:CallInstallFailed:',
                    gaEvent: 'call_install_failed_',
                };
            }

            const { outcome } = await deferredPrompt.prompt();

            if (outcome === 'accepted') {
                updatePwaStatus('Installed');
                setDeferredPwaPrompt(null);
                recordPromptEngagement();
                updatePushLead?.({ pwa_install: 'true' });

                return {
                    gameGaEvent: 'PWA:Installed:',
                    gaEvent: 'installed_',
                };
            }

            updatePwaStatus('NotInstalled');
            recordPromptEngagement();

            if (outcome === 'dismissed') {
                return {
                    gameGaEvent: 'PWA:InstallDeclined:',
                    gaEvent: 'install_declined_',
                };
            }
        },
        [recordPromptEngagement, updatePwaStatus]
    );

    return {
        pwaStatus,
        deferredPwaPrompt,
        pwaPromptAttempt,
        showInstallPrompt,
    };
};
